export type PlayerGender = 'male' | 'female';

export type CoupleDynamicId =
  'male_female' | 'female_male' | 'female_female' | 'male_male';

export interface CoupleDynamicOption {
  id: CoupleDynamicId;
  label: string;
  description: string;
  player1Gender: PlayerGender;
  player2Gender: PlayerGender;
}

export const COUPLE_DYNAMIC_OPTIONS: CoupleDynamicOption[] = [
  {
    id: 'male_female',
    label: 'Man & Woman',
    description: 'You play as the man, partner as the woman',
    player1Gender: 'male',
    player2Gender: 'female',
  },
  {
    id: 'female_male',
    label: 'Woman & Man',
    description: 'You play as the woman, partner as the man',
    player1Gender: 'female',
    player2Gender: 'male',
  },
  {
    id: 'female_female',
    label: 'Woman & Woman',
    description: 'Shared prompts until a dedicated deck ships',
    player1Gender: 'female',
    player2Gender: 'female',
  },
  {
    id: 'male_male',
    label: 'Man & Man',
    description: 'Shared prompts until a dedicated deck ships',
    player1Gender: 'male',
    player2Gender: 'male',
  },
];

export function isCoupleDynamicId(value: unknown): value is CoupleDynamicId {
  return COUPLE_DYNAMIC_OPTIONS.some(option => option.id === value);
}

export interface CoupleSetupState {
  dynamicId: CoupleDynamicId;
  player1Name: string;
  player2Name: string;
  multiplayerType: 'same_account' | 'two_accounts';
}

export function getGendersForDynamic(dynamicId: CoupleDynamicId): {
  player1: PlayerGender;
  player2: PlayerGender;
} {
  const option =
    COUPLE_DYNAMIC_OPTIONS.find(o => o.id === dynamicId) ??
    COUPLE_DYNAMIC_OPTIONS[0];
  return {
    player1: option.player1Gender,
    player2: option.player2Gender,
  };
}

/** The dynamics a player of this gender may start (the user is always player 1). */
export function dynamicsForGender(gender: PlayerGender): CoupleDynamicOption[] {
  return COUPLE_DYNAMIC_OPTIONS.filter(
    option => option.player1Gender === gender
  );
}

export function dynamicFor(
  own: PlayerGender,
  partner: PlayerGender
): CoupleDynamicId {
  return `${own}_${partner}`;
}

export function partnerGenderOf(dynamic: CoupleDynamicId): PlayerGender {
  return getGendersForDynamic(dynamic).player2;
}

export function formatPlayerGenderLabel(gender: PlayerGender): string {
  return gender === 'male' ? 'Man' : 'Woman';
}

export interface SessionCoupleSetup {
  player1_gender?: PlayerGender;
  player2_gender?: PlayerGender;
  dynamic?: CoupleDynamicId;
}

export function readCoupleSetupFromSession(
  flowState: Record<string, unknown> | null | undefined
): SessionCoupleSetup | null {
  if (!flowState || typeof flowState !== 'object') return null;
  const setup = flowState.couple_setup;
  if (!setup || typeof setup !== 'object') return null;
  return setup as SessionCoupleSetup;
}
